/* eslint-disable react/display-name */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from 'src/styles/layout.module.css';
import { Button, Form, Input, Modal, Popconfirm, Select, Space, Table, TableProps, Typography } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useForm } from 'antd/es/form/Form';
import { fetchLanguages } from 'src/features/languages/languages.state';
import { useLanguagesValues } from 'src/features/languages/languages.hooks';
import { CollectionLocale } from 'src/features/collections/collections.types';
import { useCollectionLocalesById } from 'src/features/collections/collections.hooks';
import {
  createCollectionLocale,
  deleteCollectionLocale,
  fetchCollectionLocales,
  updateCollectionLocale,
} from 'src/features/collections/collections.state';

const newLocale = { name: '', description: '', code: '', id: '' };

const CollectionContentLocalesTab: React.FC = () => {
  const { collectionId } = useParams() as { collectionId: string };
  const dispatch = useDispatch();
  const locales = useCollectionLocalesById(collectionId);
  const languages = useLanguagesValues();
  const [selectedLocale, setSelectedLocale] = useState<CollectionLocale>(newLocale);

  useEffect(() => {
    dispatch(fetchLanguages({}));
    dispatch(fetchCollectionLocales({ collectionId }));
  }, [dispatch, collectionId]);

  const [createMode, setCreateMode] = useState<boolean>(false);
  const handleCreateButtonClick = useCallback((locale?: CollectionLocale) => {
    setSelectedLocale(locale || newLocale);
    setCreateMode(m => !m);
  }, []);

  const tableConfig = useMemo<TableProps<CollectionLocale>>(
    () => ({
      dataSource: locales || [],
      rowKey: values => values?.id,
      columns: [
        {
          title: 'Имя',
          dataIndex: 'name',
          key: 'name',
        },
        {
          title: 'Язык',
          dataIndex: 'code',
          key: 'code',
        },
        {
          title: 'Действия',
          dataIndex: 'action',
          align: 'center',
          width: 100,
          render: (_, locale) => (
            <Button onClick={() => handleCreateButtonClick(locale)}>
              <EditOutlined />
            </Button>
          ),
        },
      ],
    }),
    [handleCreateButtonClick, locales]
  );
  const handleOk = async () => {
    const data = {
      code: selectedLocale.code,
      name: selectedLocale.name,
    };
    if (selectedLocale.id === '') {
      await dispatch(
        createCollectionLocale({
          collectionId,
          data,
        })
      );
    } else {
      await dispatch(
        updateCollectionLocale({
          collectionId,
          localeId: selectedLocale.id,
          data,
        })
      );
    }
    handleCancel();
  };
  const handleCancel = () => {
    setCreateMode(m => !m);
    setSelectedLocale(newLocale);
    form.resetFields();
  };
  const handleRemove = async () => {
    await dispatch(
      deleteCollectionLocale({
        collectionId,
        localeId: selectedLocale.id,
      })
    );
    handleCancel();
  };
  const [form] = useForm();

  return (
    <Space direction={'vertical'} style={{ width: '100%' }}>
      <Modal
        title='Перевод'
        visible={createMode}
        footer={[
          <Space key={'footer'} style={{ width: '100%', justifyContent: 'space-between' }}>
            <Popconfirm title='Уверенны, что хотите удалить?' onConfirm={handleRemove}>
              <Button danger disabled={selectedLocale.id === ''}>
                Удалить
              </Button>
            </Popconfirm>
            <Space>
              <Button key='back' onClick={handleCancel}>
                Отмена
              </Button>
              <Button key='submit' type='primary' onClick={handleOk}>
                Сохранить
              </Button>
            </Space>
          </Space>,
        ]}
        destroyOnClose
      >
        <Form
          layout='vertical'
          form={form}
          validateTrigger='onBlur'
          onFinish={handleOk}
          initialValues={{ ...selectedLocale }}
        >
          <Form.Item name='code' label={'Язык'}>
            <Select
              autoFocus
              showSearch
              value={selectedLocale.code}
              onChange={value =>
                setSelectedLocale(nd => ({
                  ...nd,
                  code: value,
                }))
              }
            >
              {languages.map(l => (
                <Select.Option key={l.code} value={l.code}>
                  {l.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name='name' label={'Название'}>
            <Input
              type='text'
              name='name'
              placeholder='Введите название'
              onChange={ev => setSelectedLocale(e => ({ ...e, name: ev.target.value }))}
            />
          </Form.Item>
        </Form>
      </Modal>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={4}>Переводы</Typography.Title>
        <Button onClick={() => handleCreateButtonClick()}>
          <PlusOutlined />
        </Button>
      </Space>
      <div className={styles.content}>
        <Table {...tableConfig} sticky showSorterTooltip={false} />
      </div>
    </Space>
  );
};
export default CollectionContentLocalesTab;
